import { useEffect, useRef } from 'react';

import useEffectEvent from 'hooks/useEffectEvent';

import { useIsOneTrustInitialized } from './useIsOneTrustInitialized';

const readActiveGroups = (): string => window.OnetrustActiveGroups ?? '';

const readGpcSignal = (): boolean =>
  !!(navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl;

export type ConsentSnapshot = {
  /** Raw `OnetrustActiveGroups`, e.g. `",C0001,C0004,"`. */
  activeCookieGroups: string;
  /** Browser-level GPC opt-out signal (`navigator.globalPrivacyControl`). */
  isGpcSignalPresent: boolean;
  /** True for the once-per-mount sync of stored consent; false for user-driven changes. */
  isInitial: boolean;
};

type UseCookieConsentEventParams = {
  onConsentChange: (snapshot: ConsentSnapshot) => void;
};

/**
 * Observes OneTrust consent and reports it to the caller. Emits once on mount
 * with the stored consent (`isInitial: true`), then again on every
 * `OnConsentChanged` (`isInitial: false`).
 */
export const useCookieConsentEvent = ({
  onConsentChange,
}: UseCookieConsentEventParams): void => {
  const isOneTrustReady = useIsOneTrustInitialized();
  const listenerRegistered = useRef(false);
  const initialSyncRan = useRef(false);

  const emitConsentChange = useEffectEvent(onConsentChange);

  useEffect(() => {
    if (!isOneTrustReady || listenerRegistered.current) {
      return;
    }

    listenerRegistered.current = true;

    if (!initialSyncRan.current) {
      initialSyncRan.current = true;
      emitConsentChange({
        activeCookieGroups: readActiveGroups(),
        isGpcSignalPresent: readGpcSignal(),
        isInitial: true,
      });
    }

    window.OneTrust?.OnConsentChanged(() => {
      emitConsentChange({
        activeCookieGroups: readActiveGroups(),
        isGpcSignalPresent: readGpcSignal(),
        isInitial: false,
      });
    });

    // OneTrust does not expose an unsubscribe API for OnConsentChanged, so no cleanup func
  }, [isOneTrustReady]);
};
